import React, { FC, useCallback, useMemo } from 'react';
import { useAppSelector } from '../../../store/configureStore';
import { getReportDetails } from '../../../store/reportDetails';
import { TABLE_ITEM_COUNT_LIMITS } from '../../../utils/constants';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import OverviewSheet from '../custom-general/overview-sheet';
import CustomSection from '../custom-general/custom-section';
import Conditional from '../../common/conditional';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import GeneralDrawing from '../custom-visual-inspection/general-drawing';
import ExecutiveSummary from '../custom-visual-inspection/executive-summary';
import ETReport from './et-report';
import PDFTablePage from '../../themed/pdf/pdfTablePage';

import * as data from '../../../utils/data';
import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface Props {
	projectDetails: models.Project;
	etReport: models.CustomETReport;
	etReportItems: models.ETIReportItem[];
	coverData: models.ExxonMobilFVIPipingCoverData;
	photoRecord: models.PDFImageProps[];
	drawing: models.PDFImageProps[];
	reportTitle: string;
}

const ETPDFReport: FC<Props> = (props) => {
	const { inspectionTypeName } = props.projectDetails;
	const { itemCountPerPage } = useAppSelector(getReportDetails);

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: props.coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: props.coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

    const mappedReportData = useMemo(() => {
		return props.etReportItems.map((item, index) => ({ id: index + 1, ...item }));
	}, [props.etReportItems]);

	const getETChunkedData = useCallback(() => {
        // If the readings are greater than the default count per page, chunk the first page by the default and render the others according to preference count.

        return utils.getItemsChunkedData(props.etReportItems, TABLE_ITEM_COUNT_LIMITS.ET, itemCountPerPage);
    }, [itemCountPerPage, props.etReportItems]);

	const overview = useCoverSheetOverview();

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={props.projectDetails}
				workOrderNumber={props.coverData.workOrderNumber}
				reportTitle={props.reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<ExecutiveSummary summary={props.etReport.remarks} />

			{utils.getChunkedData(mappedReportData, 10).map((chunk, index) => (
				<CustomSection key={index} sectionTitle="Summary">
					<div className="p-2">
						<PDFTablePage data={chunk} columns={data.getETTestingColumns()} />
					</div>
				</CustomSection>
			))}

			<GeneralDrawing generalDrawing={props.drawing} />

			<Conditional isVisible={props.etReportItems.length > 0}>
				{getETChunkedData().map((chunk, index) => (
					<ETReport 
						key={index} 
						{...(props.etReport as models.CustomETReport)} 
						data={chunk}
						sectionTitle="Eddy Current Test Report"
						shouldDisplayOverview={index === 0}
						shouldDisplayRemarks={index === getETChunkedData().length - 1}
					/>
				))}
			</Conditional>

			<Conditional isVisible={props.etReportItems.length === 0}>
				<ETReport 
					{...(props.etReport as models.CustomETReport)} 
					data={[]} 
					sectionTitle="Eddy Current Test Report"
					shouldDisplayOverview
					shouldDisplayRemarks
				/>
			</Conditional>

			<CustomGeneralPhotos label="Photographic Report" photos={props.photoRecord} />
		</React.Fragment>
	);
};

export default withPDF(ETPDFReport);
